import { UiService } from './../../services/ui.service';
import { ApiService } from './../../services/api.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { AuthStateService } from '../../services/auth-state.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { AcModel } from '../../models/ac.model';
import { TranslocoService } from '@ngneat/transloco';
import {
  faExclamationTriangle,
} from '@fortawesome/pro-light-svg-icons';
import { Validator } from 'projects/core/src/lib/helpers/validator';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { environment } from 'projects/environments/environment';

enum LoginMode {
  Login = 'login',
  AcList = 'aclist',
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  public loginForm: UntypedFormGroup;
  public errors = null;
  public busy = false;
  public ssoLoading = false;
  public messages: Array<any> = [];
  public mode = LoginMode.AcList;
  public acs: Array<AcModel> = [];
  public payload: any = null;

  public faExclamationTriangle = faExclamationTriangle;
  public LoginMode = LoginMode;

  public loading = true;
  public ssoActive = false;
  public ssoRedirect: string = null;

  public version = environment.version;

  constructor(
    private fb: UntypedFormBuilder,
    private api: ApiService,
    private authState: AuthStateService,
    private ui: UiService,
    public transloco: TranslocoService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, CustomValidators.email()]],
      password: ['', [Validators.required]],
      ac: [''],
    });
  }

  ngOnInit(): void {
    this.authState.logout();
    this.initWithBrowserLanguage();
    this.ui.setFormFocus();
    this.api.init().subscribe({
      next: (result: any) => {
        this.loginForm.reset();
        this.authState.login(result.data.access_token, '/login');
        this.loadAcList(true);
        if (result.data.sso_active === true) {
          this.ssoActive = true;
          this.ssoRedirect = result.data.sso_redirect;
        }
      },
      error: (error: any) => {
        this.ssoActive = !!error.sso_active;
        this.ssoRedirect = error.sso_redirect;
        this.loading = false;
      }
    });

  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.loginForm.valid === true) {
      this.loadAcList();
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.messages.error_on_form'), this.transloco.translate('common.messages.error'));
      FormHelper.markFormAsDirty(this.loginForm);
    }
  }

  public selectAc(ac: AcModel): void {
    this.login(ac);
  }

  public cancelAcList(): void {
    this.setAclistMode();
  }

  public gotoSSO(): void {
    window.location.href = this.ssoRedirect + '&RelayState=/runner';
  }

  private loadAcList(withToken = false): void {
    let request = null;
    if (withToken === true) {
      request = this.api.aclistWithToken();
      this.loading = true;
    } else {
      this.busy = true;
      this.payload = {
        email: this.loginForm.get('email').value,
        password: this.loginForm.get('password').value,
      };
      request = this.api.aclist(this.payload);
    }
    request.subscribe({
      next: (result: any) => {
        this.acs = [];
        result.data.forEach((ac: any) => {
          this.acs.push(new AcModel().deserialize(ac, false));
        });
        this.setLoginMode();
      },
      error: (error: any) => {
        this.ui.showMessage('error', error.message, this.transloco.translate('common.messages.error')); // @translate server
        this.ssoActive = !!error.sso_active;
        this.ssoRedirect = error.sso_redirect;
      }
    }).add(() => {
        this.busy = false;
        this.loading = false;
    });
  }

  private login(ac: AcModel): void {
    this.busy = true;
    let request = null;
    if (this.authState.isLoggedIn()) {
      request = this.api.changeAc(ac.id);
    } else {
      this.payload = {
        email: this.loginForm.get('email').value,
        password: this.loginForm.get('password').value,
        ac: ac.id
      };
      request = this.api.login(this.payload);
    }
    request.subscribe({
      next: (result: any) => {
        this.loginForm.reset();
        this.payload = null;
        this.authState.login(result.access_token, '/ac/dashboard');
      },
      error: (error: any) => {
        this.ui.showMessage('error', error.message, this.transloco.translate('common.messages.error')); // @translate server
        this.ssoActive = !!error.sso_active;
        this.ssoRedirect = error.sso_redirect;
      }
    }).add(() => {
        this.busy = false;
    });
  }

  private setAclistMode(): void {
    this.payload = null;
    this.loginForm.get('email').enable();
    this.loginForm.get('password').enable();
    this.loginForm.get('ac').setValidators(null);
    this.loginForm.get('ac').updateValueAndValidity();
    this.mode = LoginMode.AcList;
    this.authState.logout();
  }

  private setLoginMode(): void {
    this.loginForm.get('email').disable();
    this.loginForm.get('password').disable();
    this.loginForm.get('ac').setValidators([Validators.required]);
    this.loginForm.get('ac').updateValueAndValidity();
    this.mode = LoginMode.Login;
  }

  private initWithBrowserLanguage(): void {
    let language = this.transloco.getDefaultLang();
    const browserLanguage = navigator.language || (navigator as any).userLanguage;
    const matches = browserLanguage.match(/([a-zA-Z]{2}).*/);
    if (Validator.isString(matches[1])) {
      language = matches[1];
    }
    if (language === 'de') {
      this.transloco.setActiveLang('de');
    } else {
      this.transloco.setActiveLang('en');
    }
  }

}
